import {
    OptionsPopoverOption,
    WaterfallChart,
    ScoreCard,
    WhiteBox,
    WaterfallData,
    FoodsIllustrations,
} from "@foodpilot/foods";
import { Stack, styled, Typography } from "@mui/material";
import { t } from "i18next";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";

export const ScoreWaterfall = () => {
    const {
        selectedScore,
        actionScores,
        allScoreWithVariations,
        actionPlanData,
        persistedOptions,
        actionPlanMetadata,
    } = useActionsContext();
    const data = allScoreWithVariations.map((score) => {
        return {
            ...score,
            onClick: (option: OptionsPopoverOption) => {
                score.onClick();
            },
        };
    });

    const apData = actionPlanData.find((i) => i.scoreId === selectedScore?.scoreId);
    const precision = selectedScore?.precision ?? 0;

    const endScores = actionScores?.end ?? {};
    const differences: WaterfallData[] = Object.entries(endScores).flatMap(([csrKey, actions]) => {
        const startingScores = actionScores?.start[csrKey] ?? { whitelistScore: null };
        const _score = ScoreService.substractScore(
            startingScores?.whitelistScore ?? null,
            actions?.whitelistScore ?? null,
        );
        const score = ScoreService.normalizeScore(_score, selectedScore?.precision ?? 0).value;
        return [
            {
                label: csrKey,
                value: score ? score : 0,
            },
        ];
    });

    const start = {
        label: apData?.metadata.startingPost.scoreByLabel ?? "",
        value: ScoreService.normalizeScore(apData?.metadata.startingPost.score ?? null, precision).value ?? 0,
    };

    const lastScoreWithoutAP = apData?.metadata.scoresWithoutActionPost.lastItem;
    const lastSimulation =
        actionPlanMetadata.isGrowthDisabled ? undefined : (
            {
                label: persistedOptions.withoutPlanName.toLowerCase(),
                value: ScoreService.normalizeScore(lastScoreWithoutAP?.score ?? null, precision).value ?? 0,
            }
        );
    const target = {
        label: persistedOptions.withPlanName.toLowerCase(),
    };

    return (
        <WhiteBox>
            <Stack>
                <ScoreCard
                    header={{
                        selectedItemId: selectedScore !== undefined ? selectedScore.id : 1,
                        options: data,
                        variant: "secondary",
                        orientation: "horizontal",
                    }}
                    disableFooter={true}
                >
                    {differences.length === 0 ?
                        <EmptyCard
                            title={t("actionPlan.preview.error.title")}
                            subtitle={t("actionPlan.preview.error.description")}
                        />
                    :   <WaterfallChart
                            reference={start}
                            height={"600px"}
                            unit={selectedScore?.unit}
                            start={lastSimulation}
                            values={differences.map((d) => ({ ...d, value: d.value ?? 0 }))}
                            target={target}
                            forcedPrecision={selectedScore?.precision}
                            textOptions={{
                                setOfActionsLabel: t("actionPlan.common.allActions"),
                                goalProgressLabelDescription: t("actionPlan.common.actionsProgressLabel"),
                            }}
                        />
                    }
                </ScoreCard>
            </Stack>
        </WhiteBox>
    );
};

type EmptyCardProps = {
    direction?: "row" | "column";
    title: string;
    subtitle: string;
};

const EmptyCard = (props: EmptyCardProps) => {
    const { direction = "row", title, subtitle } = props;
    return (
        <EmptyComparisonCard direction={direction} key={`empty-card-${direction}-${title}-${subtitle}`}>
            <FoodsIllustrations illustration="notEnoughData" />
            <Stack gap={1}>
                <Typography variant="h4">{title}</Typography>
                <Typography variant="body">{subtitle}</Typography>
            </Stack>
        </EmptyComparisonCard>
    );
};

export const EmptyComparisonCard = styled(WhiteBox)<{ direction: "row" | "column" }>(
    ({ theme, direction = "column" }) => ({
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: theme.spacing(4),
        flexDirection: direction,
        padding: theme.spacing(4),
        textAlign: direction === "row" ? "left" : "center",
    }),
);
